import { useState, useEffect } from "react";
import { ButtonPopup, ButtonSiguiente, Select } from "../../ui";
import { safeFetch } from "@/api/safeFetch";
import { useNavigate } from "react-router-dom";

interface Profesion {
  profesion_id: number;
  nombre_profesion: string;
}

const BorrarProfesiones: React.FC = () => {
  const [profesiones, setProfesiones] = useState<Profesion[]>([]);
  const [selectedProfesionId, setSelectedProfesionId] = useState<number | null>(null);
  const [selectedNombre, setSelectedNombre] = useState<string | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  const token = localStorage.getItem("token");

  // Cargar profesiones
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchProfesiones = async () => {
      try {
        const res = await safeFetch("/api/profesiones", { signal });
        if (!res.ok) throw new Error("Error cargando profesiones");
        const data = await res.json();
        setProfesiones(data);
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setError(err.message || "Error al cargar profesiones");
        }
      }
    };

    fetchProfesiones();

    return () => controller.abort();
  }, []);

  /* 
    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const id = parseInt(e.target.value);
      setSelectedProfesionId(id);
      const prof = profesiones.find((p) => p.profesion_id === id);
      setSelectedNombre(prof?.nombre_profesion || null);
    };
   */
  const handleDelete = async () => {
    if (!selectedProfesionId) return;
    setLoading(true);
    try {
      const res = await safeFetch(`/api/borrarProfesiones/${selectedProfesionId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error("Error al borrar la profesión");

      setSuccess("Profesión borrada correctamente 🎉");
      setProfesiones(profesiones.filter((p) => p.profesion_id !== selectedProfesionId));
      setSelectedProfesionId(null);
      setSelectedNombre(null);
      setShowAlert(false);
    } catch (err: any) {
      setError(err.message || "Ocurrió un error.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      setProfesiones([]);
      setSelectedProfesionId(null);
      setSelectedNombre(null);
      setShowAlert(false);
      setLoading(false);
      setError("");
      setSuccess("");
    };
  }, []);


  return (
    <div className="w-full h-screen flex items-center justify-center relative bg-zinc-500 p-4">

      {/* Botón volver */}
      <div className="absolute top-4 left-4">
        <ButtonSiguiente
          onClick={() => navigate("/backoffice")}
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
        >
          ⬅ Volver
        </ButtonSiguiente>
      </div>

      {/* Contenedor principal */}
      <div className="w-full max-w-md p-6 bg-zinc-400 shadow-md shadow-yellow-600 rounded-xl flex flex-col items-center gap-6 border-zinc-600 border-r-4 border-t-2">
        <h2 className="text-2xl font-bold text-center mb-2">Borrar Profesiones</h2>

        {error && <p className="text-red-600">{error}</p>}
        {success && <p className="text-green-600">{success}</p>}

        <div className="w-full flex flex-col items-center gap-4">
          {/* Select */}
          <div className="w-full">
            <label className="font-medium block mb-1">Selecciona la Profesión</label>
            <Select
              value={selectedProfesionId ?? ""}
              onChange={(e) => {
                const id = Number(e.target.value);
                setSelectedProfesionId(id);
                const prof = profesiones.find((p) => p.profesion_id === id);
                setSelectedNombre(prof?.nombre_profesion || null);
              }}

              options={[
                { value: "", label: "-- Selecciona --" }, // opción predeterminada

                ...profesiones
                  .sort((a, b) => a.nombre_profesion.localeCompare(b.nombre_profesion))
                  .map((p) => ({
                    value: p.profesion_id,
                    label: p.nombre_profesion,
                  })),
              ]}
              className="w-full border p-2 rounded bg-white"
            />
          </div>

          {/* Nombre seleccionado */}
          {selectedNombre && (
            <p className="font-semibold text-center text-lg">{selectedNombre}</p>
          )}

          {/* Botón borrar */}
          <ButtonSiguiente
            type="button"
            disabled={!selectedProfesionId || loading}
            className="bg-red-600 text-white py-2 rounded hover:bg-red-700 transition w-full"
            onClick={() => setShowAlert(true)}
          >
            {loading ? "Eliminando..." : "Borrar Profesión"}
          </ButtonSiguiente>
        </div>
      </div>

      {/* Modal de confirmación */}
      {showAlert && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white p-6 rounded-xl w-80 text-center shadow-lg">
            <p className="mb-4">¿Estás seguro de que deseas borrar esta profesión?</p>
            <div className="flex justify-between gap-4">
              <ButtonPopup
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition"
                onClick={() => setShowAlert(false)}
              >
                Volver
              </ButtonPopup>
              <ButtonPopup
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                onClick={handleDelete}
              >
                Aceptar
              </ButtonPopup>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BorrarProfesiones;
